import React, { FocusEventHandler, ReactNode, useId, useState, useEffect } from 'react';
import Select, {
	ControlProps,
	DropdownIndicatorProps,
	GroupBase,
	InputProps,
	MenuProps,
	SingleValueProps,
	StylesConfig,
	ValueContainerProps,
	components,
	createFilter
} from 'react-select';
import { Icon, IconName } from '../../foundations';
import { Text } from '../../foundations';
import styles from './InputAutocomplete.module.css';
import clsx from 'clsx';

export type InputAutocompleteOption = {
	value: string;
	label: string;
};

export type InputAutocompleteProps<T = InputAutocompleteOption> = {
	/**
	 * Indicates the name of the Input Select
	 * @default null
	 */
	name?: string;

	/**
	 * Indicates the availability of the Input Select
	 * @default null
	 */
	disabled?: boolean;

	/**
	 * Indicates the icon displayed in the Input Select
	 * @default null
	 */
	icon?: IconName;

	/**
	 * Indicates the text displayed as Input label
	 * @default null
	 */
	label?: ReactNode;

	/**
	 * Indicates the text displayed as ariaLabel
	 * @default null
	 */
	ariaLabel?: string;

	/**
	 * Indicates the error message text
	 * @default null
	 */
	errorMessage?: string;

	/**
	 * Indicates the children displayed as Information
	 * @default null
	 */
	information?: ReactNode;

	/**
	 * Indicates the options displayed
	 */
	options: T[];

	/**
	 * defaultValue
	 */
	defaultValue?: T | null;

	/**
	 * onBlur event
	 */
	onBlur?: FocusEventHandler<HTMLInputElement> | undefined;

	/**
	 * onChange event
	 */
	onChange?: (newValue: T) => void;

	/**
	 * selected value
	 */
	value?: T | null;

	/**
	 * Styles of the container of the input
	 */
	className?: string;

	/**
	 * Styles of the positions in the input select menu
	 * @default fixed
	 */
	menuPosition?: 'fixed' | 'absolute';
};

/**
 * Hook to detect screen size
 */
const useScreenSize = () => {
	const [isSmallScreen, setIsSmallScreen] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			setIsSmallScreen(window.innerWidth <= 768);
		};

		window.addEventListener('resize', handleResize);
		handleResize();

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	return isSmallScreen;
};

const selectAutocompleteStyles = (
	errorMessage: string | undefined,
	disabled?: boolean,
	isSmallScreen?: boolean
): StylesConfig<unknown, boolean, GroupBase<unknown>> | undefined => {
	return {
		control: (_, state) => {
			const stateAugmented = state as ControlProps & { selectProps: { icon: string; label: ReactNode } };
			const focusedPadding = isSmallScreen
				? '1.5625rem 0.5rem 0.6875rem' // xs y sm
				: '1.5625rem 1rem 0.6875rem'; // md, lg, xl

			const defaultPadding = '0rem 0.5rem';
			return {
				display: 'flex',
				width: '100%',
				height: '3.5rem',
				padding: state.isFocused && Boolean(stateAugmented.selectProps.label) ? focusedPadding : defaultPadding,
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: '0.5rem',
				outline: 'none',
				flexShrink: 0,
				fontSize: '1rem',
				fontFamily: 'var(--font-families-iberia-headline)',
				color: 'var(--grey-700-66, #666)',
				border: state.isFocused
					? '0.063rem solid var(--primary-orange, #D68726)'
					: errorMessage
						? '0.063rem solid var(--primary-red-700, #A8191A)'
						: '0.063rem solid var(--grey-400-cc, #CCC)',
				background: disabled ? 'var(--grey-200-ee, #EEE)' : 'var(--neutrals-white, #FFF)',
				'@media only screen and (width < 768px )': {
					height: '2.5rem'
				}
			};
		},
		indicatorSeparator: (baseStyles) => ({
			...baseStyles,
			display: 'none'
		}),
		dropdownIndicator: (baseStyles, state) => ({
			...baseStyles,
			transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
			transition: 'transform 0.25s'
		}),
		menuPortal: (baseStyles) => ({
			...baseStyles,
			zIndex: 10
		}),
		menu: (baseStyles) => ({
			...baseStyles,
			zIndex: 10,
			margin: 0
		}),
		singleValue: (baseStyles) => ({
			...baseStyles,
			position: 'absolute',
			left: 0,
			marginLeft: 0,
			overflow: 'visible',
			color: 'var(--grey-800-33)',
			fontFamily: 'var(--font-families-iberia-headline)',
			fontSize: '1rem',
			'&::first-letter': {
				textTransform: 'uppercase'
			}
		}),
		option: (baseStyles, state) => ({
			display: 'flex',
			width: '100%',
			padding: '0.5rem 1rem',
			alignItems: 'flex-start',
			gap: '0.5rem',
			background: state.isFocused ? 'var(--grey-100-fa, #FAFAFA)' : 'var(--neutrals-white, #FFF)',
			'&::first-letter': {
				textTransform: 'uppercase'
			}
		}),
		menuList: (baseStyles) => ({
			...baseStyles,
			outline: 'none',
			background: 'var(--neutrals-white, #FFF)',
			boxShadow: '0 0.25rem 0.25rem 0 rgba(0, 0, 0, 0.25)'
		}),
		valueContainer: (baseStyles) => ({
			...baseStyles,
			padding: '0',
			overflow: 'visible',
			display: 'flex',
			flexDirection: 'column',
			alignContent: 'flex-start',
			justifyContent: 'space-around'
		}),
		input: (baseStyles) => ({
			...baseStyles,
			position: 'absolute',
			left: 0,
			marginTop: '0rem',
			height: '1rem',
			padding: 0,
			margin: 0,
			maxWidth: '100%',
			color: 'var(--grey-800-33, #333)',
			overflow: 'hidden'
		})
	};
};

const DropdownIndicator = (props: DropdownIndicatorProps & { selectProps: { isFocus: boolean; label: ReactNode } }) => {
	const labelOnTop = props.selectProps.isFocus || props.selectProps.value;

	return (
		<components.DropdownIndicator
			className={clsx(styles.inputSelectArrowIcon, {
				[styles.inputSelectArrowIconFocused]: labelOnTop && Boolean(props.selectProps.label)
			})}
			{...props}
		>
			<Icon name="caret-abajo"></Icon>
		</components.DropdownIndicator>
	);
};

const Control = (props: ControlProps & { selectProps: { icon: IconName | undefined; label: ReactNode } }) => {
	return (
		<>
			<components.Control
				{...props}
				className={clsx({ [styles.paddingControl]: props.getValue().length > 0 && Boolean(props.selectProps.label) })}
			>
				{props.selectProps.icon ? (
					<Icon size={24} className={styles.inputSelectIcon} name={props.selectProps.icon}></Icon>
				) : null}
				{props.children}
			</components.Control>
		</>
	);
};

const Menu = (props: MenuProps) => {
	return (
		<>
			<components.Menu {...props} className={styles.animatedMenu} />
		</>
	);
};

const ValueContainer = (props: ValueContainerProps) => {
	return <components.ValueContainer {...props}>{props.children}</components.ValueContainer>;
};

const Input = (
	props: InputProps & { selectProps: { isFocus: boolean; label: ReactNode; maxLength: number; name: string } }
) => {
	return (
		<>
			{props.selectProps.label && (
				<label
					className={
						props.selectProps.isFocus || props.selectProps.value
							? styles.inputSelectLabelOpen
							: styles.inputSelectLabelClosed
					}
					id={props.selectProps['aria-labelledby']}
				>
					{props.selectProps.label}
				</label>
			)}
			<components.Input
				{...props}
				maxLength={props.selectProps.maxLength}
				data-testid={props.selectProps.name}
				autoComplete="off"
			/>
		</>
	);
};

const SingleValue = (props: SingleValueProps & { selectProps: { label: string } }) => {
	return <components.SingleValue {...props}>{props.children}</components.SingleValue>;
};

const removeDuplicates = (options: InputAutocompleteOption[]) => {
	const uniqueOptions = new Map<string, InputAutocompleteOption>();
	options.forEach((option) => {
		uniqueOptions.set(option.label, option);
	});
	return Array.from(uniqueOptions.values());
};

export const InputAutocomplete = React.forwardRef(
	(
		{
			name,
			disabled,
			value,
			defaultValue,
			information,
			errorMessage,
			className,
			icon,
			label,
			ariaLabel,
			options,
			menuPosition = 'fixed',
			onChange,
			onBlur
		}: InputAutocompleteProps,
		// eslint-disable-next-line
		ref: React.Ref<any> | undefined
	) => {
		const [isFocus, setIsFocus] = useState(false);
		const identifier = useId();
		const isSmallScreen = useScreenSize();
		const [maxLength, setMaxLength] = useState<number | undefined>();
		const filteredOptions = removeDuplicates(options);

		useEffect(() => {
			setMaxLength(options.reduce((acc, curr) => (curr.label.length > acc ? curr.label.length : acc), 0));
		}, [options]);

		return (
			<div className={className}>
				<Select
					id={identifier + '-input'}
					aria-errormessage={errorMessage ? identifier + '-error' : undefined}
					aria-labelledby={identifier + '-label'}
					aria-invalid={Boolean(errorMessage)}
					ref={ref}
					icon={icon}
					placeholder=""
					label={label}
					aria-label={ariaLabel}
					defaultValue={defaultValue}
					value={value}
					name={name}
					isDisabled={disabled}
					options={filteredOptions}
					isFocus={isFocus}
					onFocus={() => {
						setIsFocus(true);
					}}
					onChange={(e) => {
						const newValue = e as InputAutocompleteOption;
						onChange?.(newValue);
					}}
					onBlur={(e) => {
						onBlur ? onBlur(e) : null;
						setIsFocus(false);
					}}
					styles={selectAutocompleteStyles(errorMessage, disabled, isSmallScreen)}
					maxMenuHeight={180}
					menuPlacement="auto"
					menuPosition={menuPosition}
					noOptionsMessage={() => null}
					//@ts-expect-error react-select library doesn´t support custom properties. https://react-select.com/typescript
					components={{ DropdownIndicator, Control, ValueContainer, SingleValue, Menu, Input }}
					filterOption={createFilter({ ignoreAccents: false })}
					maxLength={maxLength}
				/>
				{errorMessage ? (
					<div className={styles.errorMessage}>
						<Text font={'verdana'} size={'lg-regular'}>
							{errorMessage}
						</Text>
					</div>
				) : null}
				{information ? <div className={styles.information}>{information}</div> : null}
			</div>
		);
	}
);

InputAutocomplete.displayName = 'InputAutocomplete';
